import styled from 'styled-components';
import type { FC } from 'react';
import { Coin } from '../../Props';
import type { DynamicActionAggregation } from '../../../apiHooks/useScoreBreakdownByUserQuery';
import { sortDynamicActions } from '../utils/sortDynamicActions';

type DynamicActionsSectionProps = {
    items: DynamicActionAggregation[];
};

export const DynamicActionsSection: FC<DynamicActionsSectionProps> = ({ items }) => {
    const sorted = sortDynamicActions(items);

    return (
        <SectionContainer>
            <SectionTitle>Recent insights</SectionTitle>
            {sorted.map((item, index) => (
                <ActionRow key={`${item.actionScoreType}-${item.actionName}-${index}`}>
                    {item.assetsUrl && (
                        <ActionIcon>
                            <img src={item.assetsUrl} alt={item.actionName} />
                        </ActionIcon>
                    )}
                    <ActionContent>
                        <ActionName>{item.actionName}</ActionName>
                        <ActionMeta>
                            <CategoryChip>{item.scoreBreakdown}</CategoryChip>
                        </ActionMeta>
                    </ActionContent>
                    <ActionScore>
                        <Coin value={item.score} />
                    </ActionScore>
                </ActionRow>
            ))}
        </SectionContainer>
    );
};

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.medium};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.heading};
  letter-spacing: 0.28px;
  margin: 0;
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const ActionIcon = styled.div`
  display: flex;
  width: 48px;
  height: 48px;
  max-width: 48px;
  max-height: 48px;
  background: #f7f8fa;
  border-radius: 50%;
  justify-content: center;
  align-items: center;
  flex-grow: 0;
  flex-shrink: 0;

  img {
    width: 32px;
    height: 32px;
  }

  @media (max-width: 768px) {
    width: 32px;
    height: 32px;
  }
`;

const ActionContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const ActionName = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.normal};
  color: ${({ theme }) => theme.colors.heading};
  font-weight: 500;
  overflow-wrap: break-word;
`;

const ActionMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
`;

const CategoryChip = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: 500;
  color: ${({ theme }) => theme.colors.heading};
  background: #eef1f9;
  border: 1px solid #d5daee;
  border-radius: 12px;
  padding: 2px 10px;
  text-transform: capitalize;
`;

const ActionScore = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;
